import { FC } from 'react'

import { RenderIf } from 'components/RenderIf'
import { useMySelector } from 'featureSliced/shared/hooks/useMySelector'
import { useTranslations } from 'featureSliced/shared/hooks/useTranslations'
import { useGetTranslatedNames } from 'featureSliced/shared/hooks/useGetTranslatedNames'
import { useGetProductPrice } from 'featureSliced/shared/hooks/useGetProductPrice/indexT'
import { findCatalogProduct } from 'featureSliced/shared/utils/findCatalogProduct'
import { convertToPrice } from 'featureSliced/shared/utils/convertToPrice'
import { Time } from 'featureSliced/widgets/TimeLocationNew/Time'
import { Location } from 'featureSliced/widgets/TimeLocationNew/Location'

import cameraIcon from 'assets/cameraIcon.svg'

import {
  WebCardLabel,
  WebCardValue,
  WebCartLine,
  WebCartLineBody,
  WebCartThumb,
  WebCartTitle,
  WebDivider,
  WebSummaryPanel,
  WebTextButton,
  WebTotalRow,
  WebTotalValue,
} from './styled'

const IS_CLICK_COLLECT = process.env.REACT_APP_IS_CLIC === '1'

type WebOrderSummaryProps = {
  /** back to the menu, where the cart column lives */
  onEdit?: () => void
}

/**
 * Read-only recap beside the checkout form. The cart itself is edited in the
 * column on the menu page, so nothing here mutates the order — it exists so the
 * customer can see what they are paying for while filling in their details.
 */
export const WebOrderSummary: FC<WebOrderSummaryProps> = ({ onEdit }) => {
  const t = useTranslations()
  const { getProductName } = useGetTranslatedNames()
  const { getProductPrice } = useGetProductPrice()

  const lines = useMySelector((state) => state.bucket.products)
  const productList = useMySelector((state) =>
    state.categories.items.flatMap((cat) => cat.products)
  )
  const { Currency } = useMySelector((state) => state.app.companyData)

  const priceOf = (productId: number, fallback: number) => {
    const product = findCatalogProduct(productList, productId)
    if (!product) return fallback
    return getProductPrice(product, product.categoryId || 0).finalPrice
  }

  const total = lines.reduce(
    (acc, line) => acc + priceOf(line.productId, line.price) * line.count,
    0
  )

  return (
    <WebSummaryPanel data-testid="web-order-summary">
      <WebCartTitle>
        <WebCardValue as="p">{t('order.yourOrder')}</WebCardValue>
        <RenderIf condition={!!onEdit}>
          <WebTextButton type="button" onClick={onEdit}>
            {t('buttons.change')}
          </WebTextButton>
        </RenderIf>
      </WebCartTitle>

      <RenderIf condition={IS_CLICK_COLLECT}>
        <Time />
        <Location />
        <WebDivider />
      </RenderIf>

      {lines.map((line) => {
        const product = findCatalogProduct(productList, line.productId)
        const unit = priceOf(line.productId, line.price)
        return (
          <WebCartLine key={line.uniqueId}>
            <WebCartThumb src={product?.imageUrl || cameraIcon} alt="" />
            <WebCartLineBody>
              <WebCardValue>
                {product ? getProductName(product) : ''}
              </WebCardValue>
              <WebCardLabel>
                {`${convertToPrice(line.count)} × ${convertToPrice(unit)}`}
              </WebCardLabel>
            </WebCartLineBody>
            <WebCardValue as="span">
              {convertToPrice(unit * line.count)}
            </WebCardValue>
          </WebCartLine>
        )
      })}

      <WebDivider />

      <WebTotalRow>
        <span>{t('common.total')}</span>
        <WebTotalValue>
          {`${convertToPrice(total)} ${Currency || ''}`}
        </WebTotalValue>
      </WebTotalRow>
    </WebSummaryPanel>
  )
}
